import { Component, LOCALE_ID, Inject, ViewChild, OnDestroy, AfterViewInit, HostListener, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { RegistrationService } from '../Services/registration.service';
import { CustomValidators } from '../custom-validators/custom-validators';
import { registrationSuccessRoute } from '../route-names';
import { Router } from '@angular/router';
import { DatePickerComponent } from '../date-picker/date-picker.component';
import { BehaviorSubject } from 'rxjs';
import { ApiAboutService } from '../Services/api-about.service';
import { ConfirmFeatureSetField, DateFeatureSetField, EmptyRegistrationFeatureSetResponse, FeatureSetField, FeatureSetPickerOptions } from '../backend-types/feature-set';
import { SearchCountryField } from '@justin-s/ngx-intl-tel-input';
import { VerifyForm } from '../Services/session.service';
import { environment } from '../../environments/environment';
import { InvisibleReCaptchaComponent } from 'ngx-captcha';
import { DialogService } from '../Services/dialog.service';
import { DialogType } from '../Services/dialog.service';


export const maxRegistrationPinLength = 16;
export const maxEmailLength = 254;   // From RFC 3696, erratum 1690.

@Component({
    selector: 'app-registration',
    templateUrl: './registration.component.html',
    styleUrls: ['./registration.component.scss'],
    standalone: false
})
export class RegistrationComponent implements OnDestroy, AfterViewInit {
  @HostListener('paste', ['$event']) blockPaste(e: ClipboardEvent) {
    const targetElement = e.target as HTMLInputElement;
    const confirmMobile = this.el.nativeElement.querySelector('#Confirm_Mobile');
    const confirmEmail = this.el.nativeElement.querySelector('#Confirm_Email');
    if (targetElement === confirmMobile) {
      this.mobilePasteNotAllowed  = true
      setTimeout(() => {
        this.mobilePasteNotAllowed  = false;
      }, 3000);
      e.preventDefault();
    }
    if (targetElement === confirmEmail) {
      this.pasteNotAllowed  = true
      setTimeout(() => {
        this.pasteNotAllowed  = false;
      }, 3000);
      e.preventDefault();
    }
  }

  // @ViewChild('captchaElem', { static: false }) captchaElem: ReCaptcha2Component | undefined;
  displaySpinner = false;
  registrationForm: FormGroup;
  fields: Array<FeatureSetField | ConfirmFeatureSetField | DateFeatureSetField> = [];
  pickerOptions?: FeatureSetPickerOptions
  deliverOnlyByEmail: boolean = false
  deliverOnlyBySMS: boolean = false
  deliverByEMAILandSMS = false
  dateField?: DateFeatureSetField;
  defaultDateParts: Set<"day" | "month" | "year"> = new Set(["day", "month", "year"]);
  removedDateFields: FeatureSetField[] = [];

  @ViewChild(DatePickerComponent) datePickerComponent!: DatePickerComponent;
  @ViewChild('captchaRef') captchaComponent!: InvisibleReCaptchaComponent;

  private submitting = false;
  recaptchaResponse = '';
  readonly recaptchaSiteKey: string = environment.recaptchaSiteKey;
  readonly isRejectedModalOpen = new BehaviorSubject<boolean>(false);
  country: string;
  SearchCountryField = SearchCountryField;
  readonly maxEmailLength = maxEmailLength;
  readonly maxRegistrationPinLength = maxRegistrationPinLength;

  emailField;
  confirmEmailField;
  phoneField;
  confirmPhoneField;
  confirmIndices: number[] = [];
  pinFieldName?: string;  errorText?: string;
  errorTitle?: string;
  alreadyRegisteredErrorText: string;
  invalidIdentityErrorText: string;
  title: string;
  pasteNotAllowed = false
  mobilePasteNotAllowed = false
  registrationOpen = true;
  deliveryPristine = false
  pastedPhone = false
  constructor(
    private dialogService: DialogService,
    private el: ElementRef,
    private readonly registrationService: RegistrationService,
    private readonly router: Router,
    readonly aboutService: ApiAboutService,
    private readonly fb: FormBuilder,
    @Inject(LOCALE_ID) readonly locale: string,
  ) {
    if(this.aboutService.registrationNotYetOpen){
      this.errorTitle = $localize`Registration Not Open Yet`;
      this.errorText = $localize`Registration will open at ${this.aboutService.startTime} ${this.aboutService.localTimeZone}.`;
      this.dialogService.openDialog(DialogType.OK, this.errorTitle, this.errorText);
      this.registrationOpen = false;
      this.router.navigate(environment.startRoute);
    } else if(this.aboutService.registrationClosed){
      this.errorTitle = $localize`Registration Closed`;
      this.errorText = $localize`Registration was open from ${this.aboutService.startTime} to ${this.aboutService.endTime} ${this.aboutService.localTimeZone}.`;
      this.dialogService.openDialog(DialogType.ERROR, this.errorTitle, this.errorText);
      this.registrationOpen = false;
      this.router.navigate(environment.startRoute);
    } else if (this.aboutService.electionClosed) {
      this.errorTitle = $localize`Election Closed`;
      this.errorText = $localize`The election was open from ${this.aboutService.startTime} to ${this.aboutService.endTime} ${this.aboutService.localTimeZone}.`;
      this.dialogService.openDialog(DialogType.ERROR, this.errorTitle, this.errorText);
      this.registrationOpen = false;
      this.router.navigate(environment.startRoute);
    }
    this.country = this.aboutService.aboutInformation.value.country
    const registrationSettings = this.aboutService.aboutInformation.value.org_feature_set.registrationSettings
    if (registrationSettings === undefined) {
      this.fields = new EmptyRegistrationFeatureSetResponse().featureSetFields.fields
      this.alreadyRegisteredErrorText =  $localize`Our records indicate that you have already registered to vote online. If you believe this is in error, please contact us.`
      this.invalidIdentityErrorText = $localize`The information provided does not match the database. If you believe this is in error, please contact us.`
      this.title = $localize`Voter Registration`
    } else {
      this.fields = registrationSettings.fields ? registrationSettings.fields : new EmptyRegistrationFeatureSetResponse().featureSetFields.fields;
      const pickerOptions = this.aboutService.aboutInformation.value.org_feature_set.pickerOptions
      const pinDeliverChannelField = this.fields.find((field) => field.isPinDeliveryChannel)
      const pinDeliveryChannelOptions = pickerOptions?.options.find(option => option.optionName === 'PinDeliveryChannel')?.optionValues;

      if (pinDeliveryChannelOptions) {        
        const containsEmail = pinDeliveryChannelOptions.includes('EMAIL');
        const containsSMS = pinDeliveryChannelOptions.includes('SMS');
        
        this.deliverByEMAILandSMS = containsEmail && containsSMS;
        this.deliverOnlyByEmail = containsEmail && !containsSMS;
        this.deliverOnlyBySMS = !containsEmail && containsSMS;
      
        if (this.deliverByEMAILandSMS) {
          this.pickerOptions = this.aboutService.aboutInformation.value.org_feature_set.pickerOptions;
        } else if (!containsEmail && !containsSMS) {
          console.error('Invalid Delivery Channel.');
        }
      }
      
      if (!this.deliverByEMAILandSMS && pinDeliverChannelField) {
        pinDeliverChannelField.isMandatory = false;
      }
      

      this.pinFieldName = this.aboutService.aboutInformation.value.org_feature_set.featureSetFields.fields.find((field) => field.isPin)?.fieldName
      this.alreadyRegisteredErrorText = registrationSettings.alreadyRegisteredErrorText ?? $localize`Our records indicate that you have already registered to vote online. If you believe this is in error, please contact us.`
      this.invalidIdentityErrorText = registrationSettings.verificationFailedErrorText ?? $localize`The information provided does not match the database. If you believe this is in error, please contact us.`
      this.title = registrationSettings.titleText ?? $localize`Voter Registration`
    }
    this.addConfirmFieldsAndDateParts()
    this.registrationForm = new FormGroup({})
    for (let field of this.fields) {
      let validators: ValidatorFn[] = []
      if (field.isMandatory) {
        validators.push(Validators.required)
      }
      if(field.isRegistrationKey){
        validators.push(Validators.maxLength(maxRegistrationPinLength));
        validators.push(Validators.pattern('[a-zA-Z0-9]*'));
      }
      if (field.fieldType === 'email') {
        validators.push(Validators.maxLength(maxEmailLength))
        validators.push(Validators.email)
        this.emailField = field
      }
      if ((field instanceof ConfirmFeatureSetField) && (field.fieldType === 'confirm_email')) {
        validators.push(Validators.email)
        validators.push(new CustomValidators(field.confirmingField).confirmEmailMatch)
        this.confirmEmailField = field
      }
      if ((field instanceof ConfirmFeatureSetField) && field.fieldType === 'confirm_phone') {
        validators.push(new CustomValidators(field.confirmingField).confirmPhoneMatch)
        this.confirmPhoneField = field
      }
      if (field.fieldType === 'phone') {
        this.phoneField = field
      }
      this.registrationForm.addControl(field.fieldName, this.fb.control(null, validators))
    }
    if(!this.aboutService.isVotingCenter()) {
      this.registrationForm.addControl('recaptcha', this.fb.control(''));
    }
    if(this.deliverByEMAILandSMS) {
      this.setupPinDeliveryChannel()
    }
    
    const emailControl = this.registrationForm.get('Email');
    const confirmEmailControl = this.registrationForm.get('Confirm_Email');
    if(emailControl && confirmEmailControl){
      emailControl.valueChanges.subscribe(valueEmail => {
        emailControl.setValue(valueEmail?.trim(), { emitEvent: false });
        confirmEmailControl.updateValueAndValidity();
      });

      confirmEmailControl.valueChanges.subscribe(valueConfirmEmail => {
        confirmEmailControl.setValue(valueConfirmEmail?.trim(), { emitEvent: false });
      });
    }   
    
    const mobileControl = this.registrationForm.get('Mobile');
    const confirmMobileControl = this.registrationForm.get('Confirm_Mobile');
    if(mobileControl && confirmMobileControl){
      mobileControl.valueChanges.subscribe(valueMobile => {
        confirmMobileControl.updateValueAndValidity();
      });
    }
  }


  ngOnDestroy() {
    this.confirmIndices.forEach(index => this.fields.splice(index, 1))
    this.toggleFieldMandate(this.emailField, false, null)
    this.toggleFieldMandate(this.confirmEmailField, false, null)
    this.toggleFieldMandate(this.phoneField, false, null)
    this.toggleFieldMandate(this.confirmPhoneField, false, null)
  }

  private addConfirmFieldsAndDateParts() {
    let newFields: ConfirmFeatureSetField[] = []
    for (let i = this.fields.length - 1; i >= 0; i--) {
      const field = this.fields[i]
      if (field.fieldType === 'email' || field.fieldType === 'phone') {
        const newField = new ConfirmFeatureSetField(field)
        this.fields.splice(i + 1, 0, newField)
        newFields.push(newField)
      }

      if (field.fieldType === 'day' || field.fieldType === 'month' || field.fieldType === 'year') {
        const dateField = this.fields.find((field) => field instanceof DateFeatureSetField)
        if (dateField) {
          (dateField as DateFeatureSetField).dateParts.add(field.fieldType)
          this.dateField = (dateField as DateFeatureSetField)
          let removed = this.fields.splice(i, 1)
          this.removedDateFields = this.removedDateFields.concat(removed)
        } else {
          const newField = new DateFeatureSetField(field, new Set([field.fieldType]))
          this.dateField = newField
          this.removedDateFields = this.fields.splice(i, 1, newField)
        }
      }
    }
    newFields.forEach(field => this.confirmIndices.push(this.fields.indexOf(field)))
  }

  private setupPinDeliveryChannel() {
    const pinDeliverChannel = this.fields.find((field) => field.isPinDeliveryChannel)
    if (pinDeliverChannel === undefined) {
      return
    }
    const control = this.registrationForm.controls[pinDeliverChannel.fieldName]  
    control.valueChanges.subscribe(value => {
      switch (value) {
        case "SMS":
          this.setupSMS();
          this.deliveryPristine = false
          break;
        case "EMAIL":
          this.setupEmail();
          this.deliveryPristine = false
          break;
        default:
          this.handleDefaultOption()
      }
    }
    )
  }

  toggleFieldMandate(field: FeatureSetField | undefined, mandatory: boolean, validators: ValidatorFn | ValidatorFn[] | null) {
    if (field) {
      field.isMandatory = mandatory
      this.registrationForm.controls[field.fieldName].setValidators(validators)
      this.registrationForm.controls[field.fieldName].updateValueAndValidity()
    }
  }

  ngAfterViewInit() {
    if(document.readyState === "complete") {
      setTimeout(() => {
        if(this.deliverOnlyByEmail){
          this.setupEmail()
        }

        if(this.deliverOnlyBySMS){
          this.setupSMS()
        }
        //Fix for WAVE error *************************************************
        const response = document.getElementById("g-recaptcha-response");
        if (response) {
          response.setAttribute('aria-label', "Recaptcha response");
          response.setAttribute("aria-hidden", "true");
          response.setAttribute("aria-readonly", "true");
        }
      }, 200);
    }
    this.resetFocus();
  }
  /*********************************************************************/
  resetFocus() {
    let focusField = document.getElementById('accessibility-button')
    let focusFieldMobile = document.getElementById('accessibility-button-mobile')
  
    if (focusField) {
      focusField.focus()
    }

    if (focusFieldMobile) {
     focusFieldMobile.focus()
    }
  }
 

  onTooltipClick(event: any) {
    event.preventDefault();
  }

  resolved(captchaResponse: string | null): void {
    if (captchaResponse === null) {
        console.error("Captcha response is null");
        return;
    }

    Promise.resolve().then(() => {
      this.registrationForm.patchValue({
        recaptcha: captchaResponse
      });
      this.onSubmitForm();
    }).catch(error => console.error("Error with captchaResponse:", error));
}

  onSubmit() {
    if(this.registrationOpen) {
      if(this.aboutService.isVotingCenter()) {
        this.onSubmitForm();
      } else {
        this.captchaComponent.execute();
      }
    }
  }

  onBlur(fieldName: string){
    const control = this.registrationForm.controls[fieldName];  
    if (control.pristine) {
     this.deliveryPristine = true
    } else {
      this.deliveryPristine = false
    }
  }

  onSubmitForm() {
    if (this.registrationForm.valid && this.registrationOpen) {
      this.submitting = true;
      this.displaySpinner = true;
      const form: VerifyForm = {keyPairs: [], pinFieldName: this.pinFieldName, locale: this.locale};
      const keys = Object.keys(this.registrationForm.controls)
      for (let key of keys) {
        const field = this.fields.find((f) => f.fieldName === key)
        if (key === 'recaptcha') {
          continue
        }
        if (field === undefined || !field.isMandatory || field.fieldType.startsWith("confirm_")) {
          continue
        }
        let value: string = this.registrationForm.get(key)?.value as string
        
        if (field.isPinDeliveryChannel && (value == "EMAIL" || value == "SMS")) {
          form.pinFieldName = undefined
        }

        if (field.fieldType === 'phone') {
          value = this.registrationForm.get(key)?.value.e164Number
        }
        if (field.fieldType === 'date') {
          if (this.removedDateFields.length > 0) {
            const dateTime = this.registrationForm.get(key)?.value
            this.removedDateFields.forEach((removed) => {
              switch (removed.fieldType) {
                case 'day':
                  value = String(dateTime['day']).padStart(2,'0')
                  break
                case 'month':
                  value = String(dateTime['month']).padStart(2,'0')
                  break
                case 'year':
                  value = String(dateTime['year']).padStart(4,'0')
                  break
              }
              const keyPair = {key: removed.fieldName, value: value}
              form.keyPairs.push(keyPair)
            })
            continue
          } else {
            let dateTime = this.registrationForm.get(key)?.value
            let dateString = String(dateTime['year']).padStart(4,'0') + '-' + String(dateTime['month']).padStart(2,'0') + '-' + String(dateTime['day']).padStart(2,'0')
            value = dateString
          }
        }
        const keyPair = {
          key: key,
          value: value
        };
        form.keyPairs.push(keyPair);
      }

      if (this.deliverOnlyByEmail) {
        form.keyPairs.push({ key: 'PinDeliveryChannel', value: 'EMAIL' });
      } else if (this.deliverOnlyBySMS) {
        form.keyPairs.push({ key: 'PinDeliveryChannel', value: 'SMS' });
      }
      
      let recaptchaString: string = "";
      if (!this.aboutService.isVotingCenter()) {
        recaptchaString = this.registrationForm.get('recaptcha')?.value as string
      }

      (this.registrationService.register(form, recaptchaString)).subscribe({
        next: (response) => {
          if (response) {
            this.registrationService.registered = true;
            this.nextScreen(response);
          }
          else {
            this.displaySpinner = false
            this.submitting = false
            const errorText = $localize`Please try again in a few minutes. Thank you`;
            const errorTitle = $localize`Registration Not Successful.`;
            this.dialogService.openDialog(DialogType.ERROR, errorTitle, errorText);
          }
        },
        error: (error) => {
          if(!this.aboutService.isVotingCenter()) {
            this.captchaComponent.resetCaptcha();
          }
          this.displaySpinner = false;
          this.submitting = false;

          if ((error.error_type !== undefined && error.error_type === 'INVALID_IDENTITY') ||
          (error.error !== undefined && error.error.error_type === 'INVALID_IDENTITY')) {
           const errorText = this.invalidIdentityErrorText;
           const errorTitle = $localize`Registration not found.`
           this.dialogService.openDialog(DialogType.ERROR, errorTitle, errorText);
          } else if ((error.error_type !== undefined && error.error_type === 'ALREADY_REGISTERED') ||
          (error.error !== undefined && error.error.error_type === 'ALREADY_REGISTERED')) {
           let errorText = ``
            if (error.time !== undefined) {
             errorText = this.alreadyRegisteredErrorWithTime(error.time);
            } else if (error.error !== undefined && error.error.time !== undefined){
             errorText = this.alreadyRegisteredErrorWithTime(error.error.time);
            } else {
             errorText = this.alreadyRegisteredErrorWithTime(error.time);
            }
            const errorTitle = $localize`Already registered.`
            this.dialogService.openDialog(DialogType.ERROR, errorTitle, errorText);
          } else if ((error.error_type !== undefined && error.error_type === 'ALREADY_LOGGED_IN') ||
          (error.error !== undefined && error.error.error_type === 'ALREADY_LOGGED_IN')) {
            this.errorText = $localize`You already have an active session. To continue, please close the active session and try again.`;
            this.errorTitle = $localize`Already Logged In.`
            this.dialogService.openDialog(DialogType.ERROR, this.errorTitle, this.errorText );
          } else if ((error.error_type !== undefined && (error.error_type === 'DECODER_STATE_LOST' || error.error_type === 'INVALID_MTE_DECODING')) ||
          (error.error !== undefined && (error.error.error_type === 'DECODER_STATE_LOST' || error.error.error_type === 'INVALID_MTE_DECODING' ))) {
            this.errorText = $localize`Your session timed out. Please try to submit again or refresh page.`;
            this.errorTitle = $localize`Registration Not Successful.`
            this.dialogService.openDialog(DialogType.ALERT, this.errorTitle, this.errorText);
          } else if ((error.error_type !== undefined && error.error_type === 'REGISTRATION_NOT_YET_OPEN') ||
          (error.error !== undefined && error.error.error_type === 'REGISTRATION_NOT_YET_OPEN')) {
            this.errorTitle = $localize`Registration Not Open Yet`;
            this.errorText = $localize`Registration will open at ${this.aboutService.startTime} ${this.aboutService.localTimeZone}.`;
            this.dialogService.openDialog(DialogType.OK, this.errorTitle, this.errorText);
          } else if ((error.error_type !== undefined && error.error_type === 'REGISTRATION_CLOSED') ||
          (error.error !== undefined && error.error.error_type === 'REGISTRATION_CLOSED')) {
            this.errorTitle = $localize`Registration Closed`;
            this.errorText = $localize`Registration was open from ${this.aboutService.startTime} to ${this.aboutService.endTime} ${this.aboutService.localTimeZone}.`;
            this.dialogService.openDialog(DialogType.ERROR, this.errorTitle, this.errorText);
            this.router.navigate(environment.startRoute);
          } else if ((error.error_type !== undefined && error.error_type === 'ELECTION_CLOSED') ||
          (error.error !== undefined && error.error.error_type === 'ELECTION_CLOSED')) {
            this.errorTitle = $localize`Election Closed`;
            this.errorText = $localize`The election was open from ${this.aboutService.startTime} to ${this.aboutService.endTime} ${this.aboutService.localTimeZone}.`;
            this.dialogService.openDialog(DialogType.ERROR, this.errorTitle, this.errorText);
            this.router.navigate(environment.startRoute);
          } else if (error.message == 'Invalid recaptcha'){
            const errorTitle = $localize`Captcha error.`;
            const errorText = $localize`Please disable pop-up blockers or try using another browser or another device.`;
            this.dialogService.openDialog(DialogType.ERROR, errorTitle, errorText);
          } else {
            console.error(error);
            const errorText = $localize`An unknown error occurred. Please contact us.`
            const errorTitle = $localize`Unknown error.`
            this.dialogService.openDialog(DialogType.ERROR, errorTitle, errorText);
          }
        },
        complete: () => {
          this.submitting = false;
          this.displaySpinner = false;
        },
      });
    }
  }

  focusErrorDialog(){
    setTimeout(()=>{  
      const focusableElements = 'button';
      const modal:any = document.querySelector('#registration-error');
      const firstFocusableElement = modal.querySelectorAll(focusableElements)[0]
      firstFocusableElement.focus()
    }, 200);
  }

  alreadyRegisteredErrorWithTime(time: string | null): string {
    if (time) {
      const date = new Date(time);
      const formattedTime = date.toLocaleTimeString(this.aboutService.locale) + ', ' +
       date.toLocaleDateString(this.aboutService.locale, {year: 'numeric', month: 'long', day: 'numeric' });
      return this.alreadyRegisteredErrorText.replace('^1', formattedTime);
    } else {
      return this.alreadyRegisteredErrorText.replace(' at ^1', '').replace(' à ^1', '').replace(' a las ^1', '');
    }
  }

  pickerOptionsFor(fieldName: string): string[] {
    if(this.pickerOptions) {
      let matchingOption = this.pickerOptions.options.find(option => option.optionName === fieldName)
     
      if (matchingOption) {
        return matchingOption.optionValues
      } else {
        return []
      }
    } else {
      return []
    }
  }

  private nextScreen(votingPin: string | boolean): Promise<boolean> {
    this.displaySpinner = false
    return this.router.navigate(registrationSuccessRoute, { state: { votingPin } });
  }

  private setupSMS() {
    if (this.phoneField && this.confirmPhoneField) {
      this.toggleFieldMandate(this.phoneField, true, Validators.required);
      this.toggleFieldMandate(
        this.confirmPhoneField,
        true,
        [Validators.required, new CustomValidators(this.confirmPhoneField.confirmingField).confirmPhoneMatch]
      );
    }
    if (this.emailField && this.confirmEmailField) {
      this.toggleFieldMandate(this.emailField, false, null);
      this.toggleFieldMandate(this.confirmEmailField, false, null);
    }
  }
  
  private setupEmail() {
    if (this.phoneField && this.confirmPhoneField) {
      this.toggleFieldMandate(this.phoneField, false, null);
      this.toggleFieldMandate(this.confirmPhoneField, false, null);
    }
    if (this.emailField && this.confirmEmailField) {
      this.toggleFieldMandate(
        this.emailField,
        true,
        [Validators.required, Validators.email, Validators.maxLength(maxEmailLength)]
      );
      this.toggleFieldMandate(
        this.confirmEmailField,
        true,
        [Validators.required, Validators.email, new CustomValidators(this.confirmEmailField.confirmingField).confirmEmailMatch]
      );
    }
  }

  private handleDefaultOption() {
    if (this.phoneField && this.confirmPhoneField) {
      this.toggleFieldMandate(this.phoneField, false, null);
      this.toggleFieldMandate(this.confirmPhoneField, false, null);
    }
    if (this.emailField && this.confirmEmailField) {
      this.toggleFieldMandate(this.emailField, false, null);
      this.toggleFieldMandate(this.confirmEmailField, false, null);
    }
  }

  disableCutPaste(event: ClipboardEvent, fieldType: string): void {
    if (fieldType === 'confirm_phone') {
      event.preventDefault(); 
      this.pastedPhone = true; 
      setTimeout(() => {
        this.pastedPhone = false;
      },3000); 

    } else {
      this.pastedPhone = false; 
    }
  }

// Trim text pasted into an input field.
handlePaste(event: ClipboardEvent): void {
  event.preventDefault();

  const trimmedText = event.clipboardData?.getData('text').trim();
  const target = event.target as HTMLInputElement;
  const controlName = target.getAttribute('id');
    if (controlName) {
      this.registrationForm.get(controlName)?.setValue(trimmedText);
    }
  }
  
}

type EmailErrorReason = 'required' | 'maxlength' | 'email' | 'other';
type ConfirmEmailErrorReason = 'required' | 'mismatch' | 'other';
